import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import api from '../utils/api';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Bot, RefreshCw } from 'lucide-react';

export default function AiBotsAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/ai-bots');
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load AI bots.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="flex" data-testid="ai-bots-admin-page">
      <Sidebar />
      <div className="flex-1 min-w-0 bg-slate-50 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl bg-rose-600 flex items-center justify-center text-white">
                <Bot size={22} />
              </div>
              <div>
                <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>AI Bots</h1>
                <p className="text-slate-500 text-sm">Every tenant's AI Assistant — usage &amp; cost across the platform.</p>
              </div>
            </div>
            <button onClick={load} className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900" data-testid="ai-bots-refresh">
              <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>

          {/* Totals */}
          {data && (
            <div className="grid grid-cols-3 gap-4 mb-6">
              <Card><CardContent className="p-4"><p className="text-2xl font-bold">{data.total_bots}</p><p className="text-xs text-slate-500">Active bots</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-2xl font-bold">{data.total_queries}</p><p className="text-xs text-slate-500">Questions answered</p></CardContent></Card>
              <Card><CardContent className="p-4"><p className="text-2xl font-bold">${(data.total_cost ?? 0).toFixed(4)}</p><p className="text-xs text-slate-500">Total cost</p></CardContent></Card>
            </div>
          )}

          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="p-8 text-center text-slate-400 animate-pulse">Loading bots…</div>
              ) : !data?.bots?.length ? (
                <div className="p-8 text-center text-slate-400">No tenant bots have been created yet.</div>
              ) : (
                <table className="w-full text-sm" data-testid="ai-bots-table">
                  <thead className="text-left text-slate-500 border-b">
                    <tr>
                      <th className="p-3">Tenant</th>
                      <th className="p-3">Plan</th>
                      <th className="p-3">Status</th>
                      <th className="p-3 text-right">Questions</th>
                      <th className="p-3 text-right">Conversations</th>
                      <th className="p-3 text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.bots.map((b) => (
                      <tr key={b.bot_id} className="border-b last:border-0 hover:bg-slate-50" data-testid={`ai-bot-row-${b.tenant_id}`}>
                        <td className="p-3 font-medium">{b.name || b.tenant_id}</td>
                        <td className="p-3 capitalize">{b.plan_level}{b.addon_active ? ' + add-on' : ''}</td>
                        <td className="p-3">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${b.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                            {b.enabled ? 'Live' : 'Off'}
                          </span>
                        </td>
                        <td className="p-3 text-right">{b.queries}</td>
                        <td className="p-3 text-right">{b.sessions}</td>
                        <td className="p-3 text-right">${(b.cost ?? 0).toFixed(4)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
